import {
  Dialog,
  MenuItem,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { ReactElement, useCallback } from "react";
import { useForm } from "react-hook-form";

import {
  CampaignMetadataFilterInput,
  MetadataFieldsFragment,
} from "~frontend/__generated__/graphql";
import CollapsibleDrawer from "~frontend/components/CollapsibleDrawer";
import { FlexBox } from "~frontend/components/FlexBox";
import { UserListSelector } from "~frontend/components/campaignMetadata/UserListSelector";
import {
  RhfTextField,
  RhfSelect,
  RhfYesNoSelect,
} from "~frontend/components/forms/react-hook-form-components";
import { RhfMultiSelectCheckbox } from "~frontend/components/forms/react-hook-form-components/RhfMultiSelectCheckbox";

export type AdvancedFilterValue = CampaignMetadataFilterInput["value"];

export function replaceSpaceWithUnderscore(value: string): string {
  return value.replace(/\s+/g, "_");
}

type MetadataFieldWithLabel = MetadataFieldsFragment & {
  label: string;
};

export function AdvancedFilterDialogue({
  open,
  close,
  setAdvancedFilters,
  metadataFields,
  metadataFilters,
}: {
  open: boolean;
  close: () => void;
  setAdvancedFilters: (filters: CampaignMetadataFilterInput[]) => void;
  metadataFields: MetadataFieldWithLabel[];
  metadataFilters: CampaignMetadataFilterInput[];
}): ReactElement {
  const defaultValues = metadataFields.reduce(
    (acc, field) => {
      switch (field.__typename) {
        case "CampaignMetadataStateField": {
          if (field.configuration.isSingleSelect) {
            acc[field.name] = "";
          } else {
            acc[field.name] = [];
          }
          break;
        }
        case "CampaignMetadataEnumField":
        case "CampaignMetadataStringField":
          acc[field.name] = "";
          break;
        case "CampaignMetadataEnumMultiField":
          acc[field.name] = [];
          break;
        case "CampaignMetadataBoolField":
          acc[field.name] = null;
          break;
        case "CampaignMetadataUserListField":
          acc[field.name] = { userId: "", fullName: "" };
          break;
        default:
          break;
      }
      return acc;
    },
    {} as Record<string, AdvancedFilterValue>
  );
  const initialValues = metadataFields.reduce(
    (acc, field) => {
      const existingFilter = metadataFilters.find(mf => mf.name === field.name);
      if (existingFilter) {
        acc[field.name] = existingFilter.value;
      } else {
        acc[field.name] = defaultValues[field.name];
      }
      return acc;
    },
    {} as Record<string, AdvancedFilterValue>
  );

  const { control, handleSubmit, reset } = useForm({
    values: initialValues,
  });

  const onApplyFilters = (data: Record<string, AdvancedFilterValue>) => {
    const metadataFilters: CampaignMetadataFilterInput[] = [];
    // Don't send blank fields to the api for the time being. If a null value
    // is sent then the api will filter for campaigns without that a metadata
    // value.
    Object.entries(data).forEach(([name, value]) => {
      if (
        value === "" ||
        value === null ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0)
      ) {
        return;
      }
      if (typeof value === "object" && "userId" in value) {
        if (value.userId !== "") {
          metadataFilters.push({ name, value });
        }
        return;
      }

      metadataFilters.push({ name, value });
    });
    setAdvancedFilters(metadataFilters);
    close();
  };

  const renderFilter = useCallback(
    (field: MetadataFieldWithLabel): ReactElement => {
      const defaultParams = {
        name: field.name,
        label: field.label,
        fullWidth: true,
        control,
      };
      switch (field.__typename) {
        case "CampaignMetadataStringField":
          return <RhfTextField key={field.id} {...defaultParams} />;
        case "CampaignMetadataStateField": {
          if (!field.configuration.isSingleSelect) {
            const options = field.configuration.values.map(value => ({
              id: value,
              label: value,
            }));
            return (
              <RhfMultiSelectCheckbox
                key={field.id}
                sx={{ mb: 2 }}
                options={options}
                {...defaultParams}
              />
            );
          }
          return (
            <RhfSelect key={field.id} withNoneOption {...defaultParams}>
              {field.configuration.values.map(option => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </RhfSelect>
          );
        }
        case "CampaignMetadataEnumMultiField": {
          const options = field.configuration.values.map(value => ({
            id: value,
            label: value,
          }));
          return (
            <RhfMultiSelectCheckbox
              key={field.id}
              sx={{ mb: 2 }}
              options={options}
              {...defaultParams}
            />
          );
        }
        case "CampaignMetadataEnumField": {
          return (
            <RhfSelect key={field.id} {...defaultParams} withNoneOption>
              {field.configuration.values.map(option => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </RhfSelect>
          );
        }
        case "CampaignMetadataBoolField": {
          return <RhfYesNoSelect key={field.id} {...defaultParams} />;
        }
        case "CampaignMetadataUserListField": {
          return <UserListSelector key={field.id} {...defaultParams} />;
        }
      }
    },
    [control]
  );

  function onCancel() {
    close();
    reset(initialValues);
  }

  const requiredFields = metadataFields
    .filter(field => field.required)
    .sort((a, b) => a.name.localeCompare(b.name));
  const nonRequiredFields = metadataFields
    .filter(field => !field.required)
    .sort((a, b) => a.name.localeCompare(b.name));
  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle>Advanced Search</DialogTitle>
      <form onSubmit={handleSubmit(onApplyFilters)}>
        <DialogContent>
          {requiredFields.map(renderFilter)}
          {nonRequiredFields.length > 0 && (
            <CollapsibleDrawer
              title=""
              collapseText="Less Metadata Filters"
              expandText="More Metadata Filters"
              startOpen={false}
              variant="slim"
              sxHeader={{ justifyContent: "flex-end", alignItems: "flex-end" }}
              sxHeading={{ fontWeight: 500 }}
            >
              {nonRequiredFields.map(renderFilter)}
            </CollapsibleDrawer>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            pt: 0,
            margin: 2,
          }}
        >
          <FlexBox justifyContent="flex-start">
            <Button
              onClick={() => reset(defaultValues)}
              color="tertiary"
              variant="contained"
            >
              Clear Selection
            </Button>
          </FlexBox>
          <FlexBox gap={1} justifyContent="flex-end" alignItems="center">
            <Button onClick={onCancel} color="tertiary" variant="contained">
              Cancel
            </Button>
            <Button type="submit" color="primary" variant="contained">
              Apply Filters
            </Button>
          </FlexBox>
        </DialogActions>
      </form>
    </Dialog>
  );
}
